import dayjs from 'dayjs';

// Modals

const applicationModals = {
  alertMessages: {
    toggleDaypart:
      'Toggle off a daypart to disable it from this individual location.',
    viewEditDayparts:
      'You can view and edit dayparts for this location after they are added to the location group',
    defaultDaypartsTiming:
      'Due to integration with RFM, these timings are not adjustable in this UI.\n\nPlease adjust daypart hour in RFM.',
    locationGoupRFM:
      'Due to integration with RFM, timings are only shown in individual dayparts.',
    locationGroupDayparts:
      'Location group dayparts will be applied to all locations added to the group. You can make location specific edits on the individual location.',
    viewEditTimings:
      'You can view and edit timings or assembly for this daypart after they are added to the location group',
    editSection:
      'Components for this section are currently applied from the original assembly. Select edit section to add different components.',
    revertSection:
      'You have made edits to this section and it is now separated from the original. If you would like to revert this section back to the original you can select revert.',
    publishAllChanges: 'Publish all saved changes to the media players',
    OOeditSchedules:
      'You can add overrides once assemblies have been added for this location',
    daypartSwitchedOff: 'Daypart is currently switched off',
    accessTimings: 'Access timings',
  },
  assemblyTagDeleteModal: {
    title: 'Are you sure you want to delete this tag?',
    subTitle:
      'Once you delete the tag, it will be removed from all assemblies.',
  },
  assemblyTagSaveModal: {
    title: 'Save your changes?',
    subTitle: 'Your updates will be saved. Do you want to proceed?',
  },
  createVariationModal: {
    title: 'You are creating a new variation of',
    subTitle:
      'You are creating a new variation of this assembly. It will apply all of the current components from the original assembly.',
  },
  convertToDraftModal: {
    title: 'Are you sure you want to convert this assembly to draft?',
    subTitle: 'You will then be able to edit this assembly.',
  },
  confirmLocationGroupModal: {
    title: 'Are you ready to continue?',
    subTitle:
      'When the next publication is made each channel for this location will commence playing the location groups schedule.',
  },
  confirmScheduleComponentModal: {
    title: 'Are you sure you want to schedule the component?',
    subTitle:
      'If overrides exist for this section, this scheduled component will take priority for the selected dates.  ',
  },
  deleteCategoryModal: {
    title: 'Are you sure you want to remove this category?',
    subTitle:
      'You are removing this product category and all products. Some products will remain if they are within other categories for this component.',
  },
  deleteAssemblyModal: {
    title: 'Are you sure you want to delete this assembly?',
    subTitle: 'This assembly will be permanently deleted.',
  },
  deleteAssetModal: {
    title: 'Are you sure want to delete this asset?',
    subTitle: 'This asset is not being used on any component.',
  },
  deleteLayoutModal: {
    title: 'Are you sure you want to delete this layout?',
    subTitle: 'This layout will be permanently deleted.',
  },
  editComponentModal: {
    title: 'Save your changes?',
    subTitle: 'Your updates will be saved. Do you want to proceed?',
  },
  deleteOverrideModal: {
    title: 'Are you sure want to delete your overrides?',
    subTitle:
      'After you delete your overrides you can publish to your media players.',
  },
  deleteScheduleComponentModal: {
    title: 'Delete schedule bundle',
    subTitleWithOutOverridesWarning: (scheduleName?: string) =>
      `Are you sure you want to delete ${scheduleName} ? This cannot be undone.`,
    subTitleWithOverridesWarning: (scheduleName?: string) =>
      `Are you sure you want to delete ${scheduleName} ? If there are overrides on the scheduled component they will also be deleted. This cannot be undone.`,
  },
  deleteScheduleItemOverridesModal: {
    title: 'Delete overrides',
    subTitle: 'Delete all your overrides scheduled on the original content.',
  },
  deleteScheduleItemModal: {
    title: 'Delete schedule component',
    subTitleWithOutOverridesWarning: (componentName?: string) => [
      `Deleting ${
        componentName ?? ''
      }. If this is the final component in the schedule, the schedule itself will also be deleted.`,
    ],
    subTitleWithOverridesWarning: (componentName?: string) => [
      `Deleting ${componentName ?? ''} will remove it and any overrides.`,
      `If this is the final component in the schedule, the schedule itself will also be deleted.`,
    ],
  },
  deleteDayPartModal: {
    title: 'Sure you want to remove this daypart?',
    subTitle:
      'Once you remove this daypart, these changes will be applied to all locations in the location group.',
  },
  editAssemblyModal: {
    title: 'Are you sure you want to edit this assembly?',
    proceed: 'Proceed',
  },
  importLocationsModal: {
    title: 'Are you sure you want to import locations?',
    subTitle: 'You will be able to assign imported locations to groups.',
  },
  locationGroupDeleteModal: {
    title: 'Are you sure you want to delete this location group?',
  },
  duplicateLocationGroupModal: {
    title: 'Duplicate location group',
    inputLabel: 'New unique location group name',
    getDuplicationNarrative: (newName: string, sourceName: string) =>
      `Location Group ${newName} created by duplication from ${sourceName}`,
    getSuccessMessage: (newName: string, sourceName: string) =>
      `'${newName}' has been successfully duplicated from '${sourceName}'`,
    genericError: 'Failed to duplicate group: Please try again.',
  },
  locationTagDeleteModal: {
    title: 'Are you sure you want to delete this tag?',
    subTitle:
      'Once you delete the tag, it will be removed from all locations and deleted.',
  },
  componentTagDeleteModal: {
    title: 'Are you sure you want to delete this tag?',
    subTitle:
      'Once you delete the tag, it will be removed from all components.',
  },
  navigateBlockerModal: {
    title: 'Are you sure you want to leave this page?',
    subTitle:
      'If you leave this page before saving, any unsaved changes will be discarded.',
    confirmText: 'Leave',
    cancelText: 'Continue editing',
  },
  previewLocationGroupsModal: {
    title: 'has been successfully assigned to these location groups',
    viewLocationGroup: 'View location group',
  },
  previewLocationModal: {
    title: 'has been successfully moved',
  },
  publicationModal: {
    title: 'Your changes have been saved',
    subTitle:
      'Do you want to publish all updates to your media players immediately?',
    locationGroupsWithoutNsnsWarning:
      '1 or more location group has no NSNs & will not be published.',
    assemblyWithoutNsnsWarning:
      '1 or more assemblies with no NSNs & will not be published',
    closeText: 'but not yet published to media players. ',
    closeMessage:
      'Your changes have been successfully saved but not yet published.',
  },
  discardModal: {
    title: 'Are you sure you want to discard your changes?',
    subTitle: 'Your changes will be discarded.',
  },
  cancelDySetupModal: {
    title: 'Are you sure you want to cancel this setup?',
    subTitle:
      'If you cancel this setup before saving, all settings will be discarded.',
  },
  publishLayoutModal: {
    title: 'Are you sure you want to save your changes?',
    subTitle: 'Saving this layout allows it to be used in assemblies.',
    confirmButton: 'Yes, confirm',
    cancelButton: 'No, cancel',
  },
  itemPerPage: 'Items per page',
  DeleteComponentModal: {
    title: 'Are you sure you want to delete this component?',
    subTitle: 'This component will be permanently deleted.',
  },
  deleteProductVariationModal: {
    title: 'Are you sure you want to delete this product variation?',
    subTitle:
      'If this product is on draft and ready to use components, it will be removed. Please preview components and assemblies that contained this product before scheduling, as the formatting may have changed.',
  },
  blockingDeletionModal: {
    title: 'Cannot delete this variation',
    subTitle:
      'This variation is currently used in other DY displays and cannot be deleted. Please replace or remove this variation from all DY displays first.',
  },
  createProductVariationFlavorModal: {
    title: 'Please provide a name for flavor',
  },
  createProductVariationTypeModal: {
    title: 'Please provide a name for variation type',
  },
  convertToDraftComponentModal: {
    title: 'Are you sure you want to convert to draft?',
    subTitle: `Only components that aren't assigned to an assembly will be converted to a draft. Once converted you will then be able to edit this component.`,
  },
  componentTagModal: {
    uniqueTagAlert: 'An item with the same unique field already exists',
    characterLimitAlert: `Character limit reached reduce characters to under 100`,
    invalidTagAlert: `Invalid tag name`,
  },
  revertAssemblyComponentModal: {
    title: 'Are you sure you want to revert this component?',
  },
  removeVariationModal: {
    title: 'Are you sure you want to remove this variation?',
    subTitle: 'Once you remove this variation, this action cannot be undone.',
  },
  removeStageAssemblyModal: {
    title: 'Are you sure you want to delete this stage assembly',
    subTitle: 'Once you remove this assembly, this action cannot be undone',
  },
  assmeblyPublicationModal: {
    title: 'Publish now?',
    subTitle:
      'You are about to publish all Creative Center updates to your media players.',
  },
  productPublicationModal: {
    title: 'Publish your updates?',
    subTitle:
      'Do you want to publish all product updates to your media players immediately?',
  },
  bulkAssmeblyPublicationModal: {
    title: 'Publish your updates?',
    subTitle:
      'Do you want to publish all assembly updates to your media players?',
    invalidOnlyMessage: 'Assemblies with no NSNs & can not be published',
  },
  saveComponentModal: {
    title: 'Are you sure you want to save this component?',
    subTitle: 'After saving:',
    saveComponentText1: 'You can add this component to assemblies',
    saveComponentText2: 'You can schedule this component',
    saveComponentText3:
      'Owner/Operators can replace existing components with this one',
    unpublishedProductWarningHeading: 'Warning: ',
    unpublishedProductWarningText:
      'This component contains unpublished products - you can publish once you save.',
    publishProductsText: 'Do you want to publish unpublished products.',
  },
  bulkReplaceComponentModal: {
    title: 'Your changes have been saved.',
    subTitle:
      'Do you want to publish all updates to your media players immediately?',
  },
  bulkReplaceComponentSaveModal: {
    title: 'Are you sure want to save your changes?',
    subTitle: 'Any overrides on component will be lost as part of this change.',
    secondSubTitle:
      'After you save your changes you can publish to your media players.',
    componentTypeMessage:
      'If you are replacing a Promotional Space component with another component type any backups will be removed.',
  },
  scheduleAssemblyModal: {
    title: 'Are you sure you want to schedule this assembly?',
    subTitle:
      'The most recently scheduled assembly takes precedence over other scheduled assemblies and default assemblies.\n\nAfter you save your changes you can publish to your media players.',
  },
  savingAssemblyModal: {
    title: 'Are you sure you want to save this assembly?',
    subTitle: 'Saving this assembly allows it to be scheduled to locations.',
  },
  tooltipMessages: {
    originalAssembly: 'Original assembly',
  },
  UpdateComponentSettingsModal: {
    title: 'Are you sure you want to update this component?',
    subtitle:
      "You've made changes to the product categories. Products that aren't included in the remaining categories will no longer appear.",
  },
  previewModal: {
    sizeDescription: ({ width, height }: any) =>
      `${width ?? ''} x ${height ?? ''} (pixels)`,
  },
  chooseLocationModal: {
    selectLocation: 'Search Location',
    chooseLocation: 'Choose location',
    confirmSelection: 'Confirm selection',
    clear: 'Clear',
    currentlySelected: 'is currently selected.',
    searchForLocation: 'Search for location',
  },
  moveLocationsModal: {
    title: 'Are you sure want to move ',
    subTitle:
      'to a new group? By moving this location, it will automatically receive all the dayparts and assemblies of the selected location group. Any overrides added by Owner/Operators will be lost.',
    moveLocationText: 'Move location',
    moveLocationTitle: ' Move location to a new location group?',
  },
  assignLocationsModal: {
    title: 'Are you sure want to assign ',
    subTitle:
      'to a new group? By assigning this location, it will automatically receive all the dayparts and assemblies of the selected location group.',
    assignLocationText: 'Assign location',
    assignLocationTitle: ' Assign location to a new location group?',
    locationGroupText: 'Select new location group',
  },
  componentSaveModal: {
    title: 'Review this component before saving.',
    subTitle:
      'One or more products that were added to the component have been deleted. Review this component before saving.',
  },
  assignLocationModal: {
    title: 'You have assigned locations to this group',
    subTitle:
      'The newly added groups in this location will now take on this group’s default assemblies. Do you want to publish these updates to your media players immediately?',
    closeMessage:
      'Your changes have been successfully saved but not yet published.',
  },
  componentListModal: {
    suggestion:
      'This view is filtered to show only components with no end date. Only Suggestions components are available.',
    recommended:
      'This view is filtered to show only components with no end date. Only Recommended components are available.',
  },
  selectAllAssemblyModal: {
    title: 'Do you want to continue?',
    subTitle:
      'You are about to select more than 200 records. This may impact performance.',
    cancelText: 'No, cancel',
    confirmText: 'Yes, confirm',
  },
  bulkStageReviewModal: {
    title: 'Are you sure want to stage your changes?',
    subTitle: 'After you stage your changes you can review and publish.',
  },
  bulkStageConfirmModal: {
    title: 'Bulk replace name.',
  },
  scheduleComponentsModal: {
    title: 'Are you sure you want to schedule the content?',
    subTitle:
      'If overrides exist for this section, this schedule will take priority for the selected dates.',
  },
  scheduleComponentsEditModal: {
    title: 'Are you sure you want to update the schedule?',
  },
  confirmationSaveOverrideModal: {
    title: 'Are you sure you want to schedule this override?',
    subTitle:
      'Overrides will display on your menu boards for the time frame you have set.',
  },
  confirmationRemovingInvalidBackupsModal: {
    title: 'Invalid backups',
    subTitle:
      'Some backups are expiring within the scheduled dates and will be removed from component.',
    confirmText: 'Ok, remove',
  },
};

const productsLabels = {
  variationType: 'Variation type *',
  productCode: 'Product code',
  productCodeRequired: 'Product code *',
  productNameRequired: 'Product name *',
  statusRequired: 'Status *',
  nutrition: 'Nutrition',
  volume: 'Volume (mL)',
  flavorRequired: 'Flavor *',
  productImageAssociatedTheme:
    'The image associated to the theme of this component has been selected by default.',
};

const categoryLabels = {
  categoryTitle: 'Category title',
  categoryTitleText: 'Category title text',
  categoryDescription: 'Category description',
  categoryDescriptionText: 'Category description text',
  borderColor: 'Border color',
  dividerColor: 'Divider color',
  borderRadius: 'Border radius',
  borderStyle: 'Border style',
  strokeWeight: 'Stroke weight',
  backgroundColor: 'Background color',
  displayOrder: 'Display order',
  column: 'Column',
  offsetTop: 'Offset top',
  productOrderOverride: 'Product order override',
  subheader: 'Subheader',
  subheaderText: 'Subheader text',
  subheaderDescription: 'Subheader description',
};

const customerStateLabels = {
  unknown: 'Unknown',
  known: 'Known',
  mobile: 'Mobile',
};

const assemblyAlerts = {
  customerUnknown:
    'If Unknown user content is configured for any of your components it will show in the preview.',
  customerKnown:
    'If Known user content is configured for any of your components it will show in the preview.',
  customerKnownMop:
    'If Mobile user content is configured for any of your components it will show in the preview.',
};

export const lang = {
  today: 'Today',
  yearly: 'Yearly',
  monthly: 'Monthly',
  weekly: 'Weekly',
  modifiedDate: 'Modified date',
  scheduledDate: 'Schedule date',
  addVariation: 'Add variation',
  addComponent: 'Add component',
  addComponentTag: 'Add component tag',
  addAdditionalCategory: 'Add additional category',
  addAdditionalColumn: 'Add additional column',
  addAdditionalComponent: 'Add additional component',
  addLocations: 'Assign locations',
  addBackup: 'Add backup',
  addBackupTooltip: 'You can only add 5 back-ups',
  addProduct: 'Add product',
  replaceProduct: 'Replace product',
  replaceProductImage: 'Replace product image',
  addNewItem: 'Add new item',
  addNewPosition: 'Add new position',
  createLayoutBeforeAssembly: 'Create a layout before starting a new assembly',
  addNewProduct: 'Add new product',
  addNewVariationType: 'Add new variation type',
  addIconText: 'Add icon & Text',
  addImage: 'Add image',
  addSupportingProductImage: 'Add supporting product image',
  addTag: 'Add tag',
  addDefault: 'Add default',
  addToLocationGroup: 'Add to location group',
  administrator: 'Administrator',
  assembly: 'Assembly',
  addAssembly: 'Add assembly',
  addAssemblyTag: 'Add assembly tag',
  defaultAssembly: 'Default assembly',
  assemblyName: 'Assembly name',
  assemblyDetails: 'Assembly details',
  allSizes: 'Apply same styling for all sizes',
  activeText: 'Active',
  addCategoryText: 'Add product categories',
  alreadyExists: 'already exists.',
  alertError: ' An error occurred, please refresh the page and try again.',
  assemblies: 'Assemblies',
  noOfAssemblies: 'No of assemblies',
  assemblyTag: 'Assembly Tag',
  assemblyTagText: 'Assembly tag',
  assemblyTags: 'Assembly tags',
  uniqueTagAlert: 'An item with the same unique field already exists',
  assemblyStates: 'Assembly states',
  assemblyState: 'Assembly State',
  about: 'About Creative Center',
  assemblyNoState: 'This assembly has no states',
  assignLocationsTo: 'Assign locations to',
  addNewFlavor: 'Add new flavor',
  activeDays: 'Active days',
  flavorName: 'Flavor name',
  back: 'Back',
  backHome: 'Back home',
  backSoonLabel: 'Back soon',
  backSoon: 'Back soon text',
  badging: 'Badging',
  backup: 'Backups',
  defaultDurationBackup:
    'Position duration will be extended if video duration is longer',
  defaultDurationBackupAssembly:
    'Duration will be extended if video duration is longer',
  backSoonText:
    'These settings are applicable when the global configurations outage strategy is back soon.',
  badgeColor: 'Badge color',
  badgeBackground: 'Badge background',
  badgeSize: 'Badge size',
  badgeType: 'Badge type',
  bulkReplaceComponent: 'Bulk replace a component',
  selectReplacementComponent: 'Select replacement component',
  bulkReplace: 'Bulk replace',
  bulkAction: 'Bulk action',
  bulkReplaceName: 'Bulk replace name',
  breakfast: 'breakfast',
  backupWarningTips: 'You can only add 5 back-ups',
  viewLayouts: 'View layouts',
  layouts: 'Layouts',
  layout: 'Layout',
  selectedText: 'Selected',
  productItemLabel: (index: number) => `Product ${index}`,
  productVariationFlavor: 'Product variation flavor',
  productVariationType: 'Product variation type',
  creativeCenterVersionSubText:
    'Creative Center is currently using the following versions:',
  barWidth: 'Bar width',
  cancel: 'Cancel',
  caption: '.png, .jpg, .jpeg, .webm or .gif',
  calendar: 'Calendar',
  calendarScheduler: 'Calendar Scheduler',
  cancelText: 'No, cancel',
  noText: 'No',
  canNotAddNewTag: 'Cannot add a new tag.',
  cannotSaveDayparts: 'Cannot save dayparts without adding assembly.',
  currenySpaceLabel: 'Space between currency and price',
  calendarDaypartTitle: 'Dayparts',
  saveNow: 'Save now?',
  saveToCreativeCenter: 'Assembly changes will be saved to Creative Center.',
  close: 'Close',
  goToStagedChanges: 'Go to staged changes',
  clear: 'Clear data',
  cod: 'COD',
  ccUI: 'CC-UI',
  ccBE: 'CC-BE',
  coatesLogin: 'COATES LOG IN',
  welcomeBack: 'Welcome back',
  chooseLoginOption: 'Choose your login option',
  loginPageErrorOccurred: 'AN ERROR OCCURRED',
  collection: 'Collection',
  columns: 'Columns',
  component: 'Component',
  components: 'Components',
  contentCreator: 'Content Creator',
  listingHeight: 'Listing height',
  displayAllVisuals: 'Display all visuals',
  daypartRequired: 'Daypart is required',
  staggered: 'Staggered',
  straight: 'Straight',
  firstProductTop: 'First Product Top',
  firstProductBottom: 'First Product Bottom',
  changesMade: 'Changes made',
  changesMadeInfo: 'Deleted content will only show ID',
  characterLimit: 'Character limit reached reduce characters to under 100',
  componentModalInfo: 'Expired components are not available',
  duplicatedAssemblyErrorMsg:
    'Assembly name is duplicated, add a unique name to continue.',
  createAssemblyInputLabel: 'Assembly name*',
  confirm: 'Confirm',
  confirmText: 'Yes, confirm',
  confirmTextAfterSave: 'Product publications',
  goToPublications: 'Go to publications',
  componentType: 'Component type',
  componentName: 'Component name',
  componentDisclaimer: 'Component disclaimer',
  componentRequireStartDate:
    'Component start date is required or has an error on scheduling date',
  componentRequireStartEndDate: 'Component start date and end date is required',
  create: 'Create',
  createComponent: 'Create component',
  createdSuccessfully: 'has been created successfully.',
  channel: 'Channel',
  createNewComponent: 'Do you want to create a new component?',
  changesSaved: 'Changes saved',
  channels: 'Channels',
  totalChannels: 'Total channels',
  unassignedChannels: 'Unassigned channels',
  channelType: 'Channel type',
  chooseGroup: 'Choose group',
  chooseDateRange:
    'Select a start date and optional end date. Only permitted dates are available for selection.',
  comboSetup: 'Combo set-up',
  codColorDrawer: 'Color scheme',
  codCustomMessaging: 'Custom messaging',
  codHeaderSection: 'Header section',
  codKnownUserMessaging: 'Known user messaging',
  codUnknownUserMessaging: 'Unknown user messaging',
  codCustomerOrderTabHeader: 'Customer order',
  codOrderConfirmationTab: 'Order confirmation',
  convertToDraft: 'Convert to draft',
  closeTime: 'Close time',
  clone: 'Clone',
  country: 'Country',
  daypartName: 'Daypart name',
  deleteGroup: 'Delete group',
  deleteCategory: 'Delete category',
  discard: 'Discard',
  disclaimerComponent: 'Disclaimer component rich text editor',
  displayProductName: 'Display product name',
  displayProductDisclaimer: 'Display product disclaimer',
  displayLabel: 'Display label',
  displayLabels: 'Display labels',
  displayPricesFromVariation: 'Display prices from variation size',
  deleteOverride: 'Delete Override',
  daypart: 'Daypart',
  date: 'Date',
  dateRange: 'Date range',
  dateRequired: 'Start date and end date are required',
  startDateRequired: 'Start date is required',
  endDateRequired: 'End date is required',
  endDateOutsideMainRange: 'End date exceeds date range.',
  startDateOutsideMainRange: 'Start date exceeds date range.',
  pastDateValidationError: 'Start date cannot be after end date',
  startDateValidation: 'Start date cannot be in the past',
  endDateValidation: 'End date cannot be in the past',
  startDateExceedError:
    'Start date cannot exceed the selected component`s expiry date.',
  endDateExceedError:
    'End date cannot exceed the selected component`s expiry date.',
  startDateConflict:
    'The start date conflicts with an existing component in this section and position. Please choose a different date.',
  deleteLocationGroup:
    'All individual locations in this group must be removed first before deleting this group.',
  deleteTag: 'To delete the tag please remove locations from tag',
  delete: 'Delete',
  deleteText: 'Delete',
  deselectAll: 'Deselect all',
  detail: 'Detail',
  details: 'Details',
  defaultAssemblyInfo:
    'Assemblies with either expiring or expired components cannot be set as the default.',
  default: 'Default',
  descriptionColumnKey: 'description',
  duplicate: 'Duplicate',
  dragImage: 'Drag image here or ',
  duplicateComponent: 'Duplicate component',
  duplicateAssembly: 'Duplicate assembly',
  displayProductImage: 'Display product image',
  duration: 'Duration (seconds)',
  edit: 'Edit',
  editProductsCategory: 'Edit Products Category',
  editSection: 'Edit section',
  editSelection: 'Edit selection',
  emptyLocationGroupAssign:
    'Looks like you don’t have any locations assigned to this group.',
  enterText: 'Enter text...',
  error: 'Error',
  editProducts: 'Edit products',
  exactMatchInfo: 'An exact match for product code will be found',
  expiredAssembly:
    'This assembly has an expired component, update component to save.',
  expiredComponent:
    'This component is currently expired, update expiry date to save.',
  componentExpiringIn: 'is about to expire in',
  day: 'day',
  dynamic: 'Dynamic',
  dy: 'DY',
  expiryDate: 'Expiry date',
  expiringIn: 'Expires in',
  editBoard: 'Edit board',
  errorFetchingData: 'Error occurred while fetching data.',
  errorDeletingScheduleOverride: 'Error deleting schedule override.',
  errorDeletingScheduleComponent: 'Error deleting scheduled component.',
  errorOccurred: 'An error has occurred.',
  export: 'Export',
  exportAllToCsv: 'Export all to csv',
  exportToCsv: 'Export to csv',
  exportSelected: 'Export selected',
  failedDeleteText: 'Failed to delete.',
  failedSaveText: 'Failed to save.',
  failedToAddLocation: 'Unable to add location.',
  failedToConvert: 'Failed to convert',
  failedToCopyAssembly: 'Failed to copy assembly link.',
  failedToCopyComponent: 'Failed to copy component link.',
  failedToCreateComponent: 'Failed to create component.',
  failedToCreateTag: 'Failed to create tag.',
  failedToCreateCategory: 'Failed to create category.',
  failedToCreateProductCategory:
    'Some products have validation issues and can`t save',
  failedToDeleteCategory: 'Failed to delete category.',
  failedToDeleteCategoryCustomErrorMessage:
    'Remove this category from all components to delete.',
  failedToDeleteComponent: 'Failed to delete component.',
  failedToDeleteTag: 'Failed to delete location tag.',
  failedToDeleteAsssemblyTag: 'Failed to delete assembly tag.',
  failedToDuplicate: 'Failed to duplicate',
  failedToReplace: 'Failed to replace.',
  failedToSave:
    'In order to save this component please ensure all fields are finalized.',
  failedToSaveAsDraft: 'Failed to save as draft.',
  failedToUpdateCategory: 'Failed to update category.',
  finalizeSettings: 'Finalize settings',
  file: 'File',
  flavor: 'Flavor',
  gcfVersionSubText: 'GCF',
  gcfPlayer: 'GCF Player',
  gcfCOD: 'GCF Cod',
  gcfComponents: 'GCF Components',
  getLink: 'Get link',
  grid: 'Grid',
  fontFamily: 'Font family',
  flexWrap: 'Flex wrap',
  fontColor: 'Font color',
  fontSize: 'Font size',
  type: 'Type',
  startFrom: 'Start from',
  fontStyle: 'Font style',
  groupAssign: 'Group assign',
  generalDetails: 'General details',
  headerAccessibilityConfigTitle: 'Accessibility settings',
  headerCustomerTriggeredEvents: 'Customer triggered events',
  headerAccessibilityConfigIconText:
    'Update accessibility settings that sit across all components',
  headerGlobalConfigIconText:
    'Update configurations that sit across all components',
  headerGlobalConfigTitle: ' Global configurations',
  inactiveText: 'Inactive',
  info: 'Info',
  imageAlignment: 'Image group alignment',
  image: 'Image',
  imageSelection: 'Image selection',
  invalidTagName: 'Invalid tag name',
  invalidDate: 'Invalid Date',
  iconText: 'Icon and text',
  lastModified: 'Last modified',
  lastModifiedInfo: `Latest location changes include changes that aren't published yet.`,
  publications: 'Publications',
  returnToPublications: 'Return to publications',
  stagedChanges: 'Staged changes',
  fromComponent: 'From component',
  toComponent: 'To component',
  componentPublications: 'Component Publications',
  assemblyPublications: 'Assembly publications',
  productPublications: 'Product publications',
  publicationType: 'contents',
  list: 'List',
  listLocationsPage: 'List Locations Page',
  listLocationsPageOO: 'List Locations Page OO',
  lastModifiedDate: 'Last modified date',
  lineHeight: 'Line height',
  margin: 'Margin',
  padding: 'Padding',
  locationGroupName: 'Location group name',
  locationGroup: 'Location group',
  location: 'Locations',
  locationGroups: 'Location groups',
  assignToLocationGroup: 'Assign to location group',
  loading: 'Loading...',
  locationAddedToGroup: 'Locations have been added to the location group.',
  locations: 'Locations',
  unassignedLocations: 'Unassigned locations',
  unassignedLocationsInfo: 'Locations with at least one unassigned channel',
  locationTagDeleteModalTitle: 'Are you sure you want to delete this tag?',
  locationTagDeleteModalSubtitle:
    'Once you delete the tag, it will be removed from all locations and deleted.',
  locationDetails: 'Location details',
  locationSchedule: 'Location schedule',
  locationTimings: 'Location timings',
  banner: 'Banner',
  editCODMessaging: 'Edit COD messaging',
  locationMessagingTooltip:
    'Update hiring banner & messaging through bulk action',
  rmhcToolTip: 'RMHC messaging in COD',
  bannerTooltip: 'Hiring banner in order confirmation',
  bulkEditMessagingTitle: 'Bulk edit messaging',
  bulkEditMessagingSummary: (bannerCount: number, messagingCount: number) =>
    `Banner active to ${bannerCount} location(s) COD Message active to ${messagingCount} location(s)`,
  YourOverridesAreBeingSaved: 'Your overrides are being saved.',
  OverridesSavedSuccessfully: 'Overrides saved successfully.',
  locationGroupDetails: 'Location group details',
  locationGroupSchedule: 'Location group schedules',
  locationGroupTimings: 'Location group timings',
  locationGroupTimingsAlert: `Location group dayparts will be applied to all locations in the group. You can make location specific edits on the individual location.`,
  failedToCreateLocationGroup: 'Failed to create location group.',
  locationName: 'Location name',
  locationCode: 'Location code',
  nolocationsAddedText: 'No locations added',
  locationStatus: 'Location status',
  localVersion: 'Local Version',
  mainHours: 'Main hours',
  maxRotationsTooltip:
    'You can only add a max of 5 rotations, please remove or replace a rotation to continue',
  maxTags: 'Maximum 50 tags',
  manageTags: 'Manage tags',
  manageAssemblyTags: 'Manage assembly tags',
  menuBoardHistory: 'Menu board history',
  notFoundText: 'No results found.',
  movedTo: 'has been moved to',
  name: 'Name',
  new: 'New',
  newLocationGroup: 'New location group',
  nsn: 'NSN',
  next: 'Next',
  noAssemblyConfigured:
    'Looks like you currently have no assemblies configured.',
  maxCharacters:
    'Search criteria too large. Please reduce your criteria and try again.',
  noComponents: 'Looks like you currently have no components',
  noComponentsMatchSectionSize:
    'No components match the selected section size.',
  noLocationsToImport: 'There were no new locations to import.',
  noMatchingAssemblies: 'No matching assemblies',
  noMatchingLocationGroup:
    'There are no location groups that match this location’s channel and/or screen configuration. Create a new group that matches this location’s configuration first.',
  noMatchingLocation:
    'There are currently no unassigned locations that match this group’s configurations. Please go to the individual location to swap the location group.',
  noResultSubText:
    'We couldnâ€™t find what youâ€™re searching for. Please try searching again.',
  noResultText: 'No results found.',
  noDaypartsEnabled: 'No dayparts enabled',
  noScheduledComponents: 'No scheduled components',
  noDefaultAssembly: 'No default assembly',
  noResultTextAssembliesSchedule:
    'There are no scheduled assemblies that match the selected replacement component',
  noLocationGroup: 'No location groups matching this channel',
  numberOfProducts: 'Number of products',
  numberOfScreens: 'Number of screens',
  numberOfComponents: 'Number of components',
  numberOfAssemblies: 'Number of assemblies',
  offline: 'Offline',
  online: 'Online',
  outOfDate: 'Out of date',
  failed: 'Failed',
  locationErrorMessage: 'Location not in Switchboard',
  ooOverrideEnabled: 'Override enabled',
  cmOverrideEnabled: 'O/O Override enabled',
  ownerOperatorOverride: 'O/O Override',
  ownerOperatorOverridePill: 'O/O',
  ownerOperatorText: 'Owner Operator',
  ownerOperatorPermissions: 'Owner operator permissions',
  allowed: 'Allowed',
  operation: 'Operation',
  outdoorPromoComponents:
    'Only outdoor Promotional Space components are available to schedule.',
  overrideDeletedSuccessfully: 'Override deleted successfully.',
  overrideSectionsText: 'Overridable sections will be clickable.',
  permissions: 'Permissions',
  overWriteAssetModal: {
    overwriteAssetModalConfirmText: 'Overwrite this asset everywhere?',
    overWriteAssetModalSubtitle: (assetName: string) =>
      ` ${assetName} already exists. Do you want to overwrite it everywhere it exists?`,
  },
  productVariant: (variantCount: number) => `${variantCount} product variant`,
  productVariants: (variantCount: number) => `${variantCount} product variants`,
  preview: 'Preview',
  expand: 'Expand',
  changeComponent: 'Change component',
  pixels: 'pixels',
  productCategoryTitleTextDelete:
    'Are you sure you want to delete the product category?',
  productDisclaimerAlertText: 'Product disclaimer must be turned on in ',
  products: 'Products',
  productCodeMissing: 'Please fill in the product code first',
  productName: 'Product name',
  nameOverride: 'Name override',
  productNameOverride: 'Product name override',
  productVariation: 'Product variation',
  productVariations: 'Product variations',
  productOverrideName: 'Product override name',
  positionSetting: {
    title: 'Default number of positions',
    idmbPosition: 'IDMB',
    odmbPosition: 'ODMB',
    mcCafePosition: 'McCafe',
    merchandiserPosition: 'Digital Merchandiser',
  },
  viewContent: 'View content',
  override: 'Override',
  overrideComponent: 'Override component',
  noBackups: 'No backups added',
  productVariationValidation:
    'All product variations must be deleted before you can delete this variation.',
  productVariationDelete: 'Delete variation',
  productCategory: 'Product category',
  componentTag: 'Component tag',
  productCategories: 'Product Categories',
  publicationSubTitle: 'Publish all updates to your media players immediately?',
  publicationTitle: 'Add publication',
  publish: 'Publish',
  publishAllChanges: 'Publish all changes',
  processPublishing: 'Your publications are being placed in queue.',
  publishFailed: 'Failed to place your publications in queue.',
  publicationDate: 'Publication date',
  publicationDateRange: 'Publication date range',
  productSetup: 'Product set-up',
  productBadgeSetup: 'Product badge set-up',
  productVariationValidateSelectAssetFailed: (assetName: string) =>
    ` ${assetName} already exists in Variation images. Please select a different asset.`,
  refresh: 'Refresh',
  reviewPageColumnLabel: {
    name: 'Name',
    nsn: 'NSN',
    daypart: 'Daypart',
    layout: 'Layout',
    defaultAssembly: 'Default assembly',
    locationName: 'Location name',
    componentName: 'Component name',
  },
  remove: 'Remove',
  removeCategory: 'Remove category',
  replace: 'Replace',
  replacePromotion: 'Replace promotion',
  scheduleComponentPromo: 'Schedule component',
  scheduleDates: 'Schedule dates',
  overrideDates: 'Override dates',
  overrideProducts: 'Override products',
  resetToDefault: 'Reset to default',
  reset: 'Reset',
  refine: 'Refine',
  results: 'Results',
  reviewChanges: 'Review changes',
  stageChanges: 'Stage changes',
  stageBulkDisplayName: (stageDisplayName: string | null) =>
    `${stageDisplayName} staged`,
  stageCreationSuccess: (bulkDisplayName: string | null) =>
    `${bulkDisplayName} has been successfully created.`,
  reviewProducts: 'Review Products',
  removeComponentTitle:
    'Are you sure you want to remove this component from the assembly?',
  replaceComponents: 'Replace component',
  removeComponent: 'Remove component',
  removeOverride: 'Remove override',
  role: 'Role',
  zeroResults: '0 results',
  restrictCategoryText: 'Restrict to the following product categories',
  redirecting: 'Redirecting...',
  searchAssemblyFilter: 'Search assembly name',
  search: 'Search',
  size: 'Size',
  sizeLabel: 'Size label',
  productInfoPrice: 'Price',
  productInfoNutrition: 'Nutrition',
  productInfoFlavor: 'Flavor',
  saveAsDraft: 'Save as draft',
  saveAndNext: 'Save & next',
  saveAndPublish: 'Save & Publish',
  save: 'Save',
  saveChanges: 'Save changes',
  saveChangesTips: 'You can publish after you save your changes',
  scale: 'Scale:',
  screenMode: 'Screen Mode:',
  screen: 'Screen',
  screens: 'Screens',
  scheduler: 'Scheduler',
  scheduled: 'Scheduled',
  scheduledComponents: 'Scheduled component',
  setAsDefault: 'Set as default',
  scheduleComponents: 'Schedule components',
  scheduleOverride: 'Schedule override',
  state: 'State:',
  staged: 'Staged',
  settings: 'Settings',
  select: 'Select',
  schedule: 'Schedule',
  unschedule: 'Unschedule',
  scheduleNewDefaultAssembly: 'Schedule new default assembly',
  selectAll: 'Select all',
  selectComponent: 'Select component',
  selectFile: 'select a file',
  selectLocations: 'Select locations',
  selectComponents: 'Select components',
  selectAssemblies: 'Select assemblies',
  selectAtLeastOneLocationGroup: 'Please select at least one location group.',
  allChannelsMustBeAssignedToSave:
    'All channels must be assigned to location groups before saving.',
  softSell: 'Soft sell',
  subTitlePublishConfirm:
    'After you save your changes you can publish to your media players.',
  subTitleConfirmProductPublication:
    'You must publish from the product publications page.',
  subtitleTextProducts:
    'If a component is restricted by product categories, this product will be removed when the component is next saved. Ready to use components will not change.',
  successfullyAdded: 'has been successfully added.',
  successfullyAddedLocation: 'Location has been added to the location group.',
  successfullyConvertedToDraft: 'has been successfully converted to a draft.',
  successfullyCopied: 'has been copied successfully.',
  successfullyCreated: 'has been successfully created.',
  successfullyDeleted: 'has been successfully deleted.',
  successfullyDuplicated: 'has been successfully duplicated.',
  successfullyImported: 'have been successfully imported.',
  successfullyPublished:
    'Your publications have been successfully placed in queue.',
  successfullySaveAsReady: 'has been successfully saved as ready.',
  successfullySavedAsDraft: 'has been successfully saved as draft.',
  successfullyScheduled: 'has successfully been scheduled.',
  successfullyUpdated: 'has been successfully updated.',
  successfullyDeleteStageAssembly:
    'Staged bulk replace request is successfully deleted',
  successfullySaveStageAssembly: 'Your changes are successfully saved.',
  failedToDeleteStageAssembly: 'Failed to delete stage assembly.',
  successfullyUpdatedDayparts:
    'Location group dayparts has been successfully updated.',
  successfullyUploaded: 'has been successfully uploaded.',
  failedToUpload: 'Failed to upload file.',
  failedToUploadMessage:
    'filename must not contain invalid characters and must be less than 255 bytes long. File extension must be in lowercase.',
  failedToUploadFile:
    'Failed to upload file please upload only one file at a time.',
  failedToUploadExtension: (extension: string) =>
    `Failed to upload file. ${extension} format is not supported. Please upload a different file.`,
  storeTimigs: 'Store timings',
  storeIsOpen: 'Store is open 24hrs',
  soldOut: 'Sold out text',
  Showing: 'Showing',
  screenConfiguration: 'Screen configuration',
  screenOrientation: 'Screen orientation',
  titleTextSave: 'Are you sure you want to save your changes?',
  titleTextAfterSave: 'Your changes have been saved',
  timeZone: 'Timezone',
  unSelectAll: 'Unselect all',
  update: 'Update',
  user: 'User',
  userRole: 'User role',
  upload: 'Upload',
  uploadProductImage: 'Upload product image',
  uptoDate: 'Up to date',
  uptoDateOffline: 'Up to date offline',
  updateInProgress: 'Update in progress',
  uploadImage: 'Upload image',
  uploadAnImage: 'Upload an image',
  uploadFromSwitchboard: 'Upload from Switchboard',
  uploadOrSelectImage: 'Upload or select an image',
  validationError: 'Must be larger than start frame',
  variation: 'variation',
  variationName: 'Variation name',
  variationAcronym: 'Variation acronym',
  viewDetails: 'View details',
  viewOnly: 'View only',
  viewText: 'View',
  viewDefault: 'View default',
  viewLocations: 'View locations',
  viewStatus: 'View status',
  youCurrentlyHaveNoLocations: 'You currently have no locations',
  yesContinue: 'Yes, continue',
  yesConfirm: 'Yes, confirm',
  yesSaveChanges: 'Yes, Save Changes',
  zone: 'Zone',
  subZone: 'Subzone',
  createComponentTitle: "Let's create a component",
  selectComponentType: 'Select a component type',
  selectSectionSize: 'Select a section size',
  layoutType: 'Layout type',
  layoutName: 'Layout name',
  layoutScreens: 'Layout screens',
  layoutBuilder: 'Layout builder',
  locationTimeZone: 'Location time zone',
  locationTag: 'Location Tag',
  locationTags: 'Location tags',
  locationTagsName: 'location_tags',
  locationOwnerTagsName: 'location_owner_tags',
  componentTags: 'Component tags',
  locationTagging: 'Location tagging',
  storeNumber: 'Store number',
  storeNo: 'Store No.',
  storeDetails: 'Store details',
  storeAddress: 'Store address',
  storeCity: 'City',
  storeRegion: 'Region',
  storeZip: 'Zip / Post code',
  storeTimeZone: 'Time zone',
  storeLocationCode: 'Location code',
  storeLocationZone: 'Location zone',
  storeLocationSubZone: 'Location subzone',
  sectionEditable: 'Section Editable:',
  beginCreating: 'Begin creating',
  dateCreated: 'Date created',
  aToZ: 'A-Z',
  zToA: 'Z-A',
  status: 'Status',
  startTime: 'Start time',
  section: 'Section',
  selected: 'selected',
  scheduleAssembly: 'Schedule assembly',
  scheduledAssembly: 'Scheduled assembly',
  scheduledAssemblyDate: 'Scheduled date',
  background: 'Background',
  componentSettings: 'Component settings',
  headerTextStyling: 'Header, text & styling',
  selectFontFormatting: 'Select font formatting',
  header: 'Header',
  headerAndDescription: 'Header & description',
  headerDescription: 'Header description',
  subheaderAndDescription: 'Subheader & description',
  pill: 'Pill',
  orientation: 'Orientation',
  openInNewTab: 'Navigate to component details',
  sizeLabelType: 'Size label type',
  sizeLabelShape: 'Size label shape',
  productFlavor: 'Product flavor',
  productPrice: 'Product price',
  productNutrition: 'Product nutrition',
  productDisclaimer: 'Product disclaimer',
  titleText: 'Title text',
  exactMatch: 'Exact match',
  marginBelow: 'Margin below',
  marginTop: 'Margin top',
  headerText: 'Header text',
  descriptionText: 'Description text',
  disclaimerText: 'Disclaimer text',
  tag: 'Tag',
  tags: 'Tags',
  tagName: 'Tag name',
  tagDetails: 'Tag details',
  time: 'Time',
  home: 'Home',
  schedules: 'Schedules',
  selectLayout: 'Select layout',
  timings: 'Timings',
  createTag: 'Create tag',
  createNewTag: 'Create new tag',
  createNewComponentTag: 'Create new component tag',
  createNewAssemblyTag: 'Create new assembly tag',
  editTag: 'Edit tag',
  continuous: 'Continuous',
  category: 'Category',
  editProduct: 'Edit product',
  configuration: 'Configuration',
  color: 'Color',
  pageDoesNotExist: "The page you're looking for doesn't exist.",
  pageNotFound: 'Page not found',
  description: 'Description',
  disclaimer: 'Disclaimer',
  productMoleculeStyling: 'Product molecule styling',
  subProductDisplayFormatType: 'Sub product display format type',
  priceLabelType: 'Price label type',
  shape: 'Shape',
  priceLabelStyling: 'Price label styling',
  priceStyling: 'Price styling',
  productImage: 'Product image',
  supportingProductImage: 'Supporting product image',
  publishOverrides: 'Publish overrides',
  publishConfirm: 'Publish',
  nutritionStyling: 'Nutrition styling',
  noData: 'No Data',
  on: 'ON',
  off: 'OFF',
  yes: 'Yes',
  no: 'No',
  with: 'with',
  actionTracking: 'Action tracking',
  noComponentMatchType: 'No components match this component type',
  startDate: 'Start date',
  endDate: 'End date',
  endDateOptional: 'End date (opt)',
  importLocationsButton: 'Import locations',
  importLocationsText: 'Import new locations from Switchboard HQ',
  outageType: 'Outage type',
  outageStyling: 'Outage styling',
  showProductDisclaimers: 'Show product disclaimers',
  selectTheme: 'Select theme',
  syncLocationsButton: 'Sync locations',
  lineupStyle: 'Lineup style',
  lineUpStyle: 'Line up style',
  pattern: 'Pattern',
  order: 'Order',
  alignment: 'Alignment',
  calories: 'Calories',
  variationImages: 'Variation images',
  variationDetails: 'Variation details',
  dyDisplay: 'DY display',
  addDyDisplay: 'Add DY display',
  saveDyDisplay: 'Save DY display',
  dyDisplaySavedNotPublished: 'DY display has been saved but not published',
  template: 'Template',
  saveThePageToEnableDyDisplay: 'Save the page to enable DY display',
  variationImagesDyTooltip: 'Selected asset will show across all DY displays',
  chooseYourPreferredDyTemplate: 'Choose your preferred DY template',
  themesAccessibility: 'Themes are managed in accessibility settings',
  asset: 'Asset',
  theme: 'Theme',
  themes: 'Themes',
  whichComponentThemesDescription:
    'Select the theme you want the components to inherit by default',
  indoor: 'Indoor',
  outdoor: 'Outdoor',
  mcCafe: 'McCafe',
  digitalMerchandiser: 'Digital Merchandiser',
  defaultThemesNarrativeMessage: (themeName: string) =>
    `Default component theme for ${themeName} Channel was updated on Accessibility settings.`,
  noProductName: 'Product name cannot be empty or contain only spaces',
  invalidProductCode: 'Invalid product code',
  addSupportingProducts: 'Add supporting products',
  addProducts: 'Add products',
  addBackups: 'Add Backups',
  selectImage: 'Select image',
  selectMore: 'Select more',
  groupImage: 'Group image',
  noImage: 'No image',
  caloriesIconText:
    'Global (all components) configuration for pipe, calorie value and calorie label',
  softSellInfo: {
    dateDescription: (start_date?: string | null, end_date?: string | null) => {
      if (start_date && end_date)
        return `The selected component's soft sell start date is ${dayjs(
          start_date,
        ).format('MM/DD/YYYY')} & will expire on ${dayjs(end_date).format(
          'MM/DD/YYYY',
        )}`;
      else if (start_date && !end_date)
        return `The selected component's soft sell start date is ${dayjs(
          start_date,
        ).format('MM/DD/YYYY')}`;
      else if (!start_date && end_date)
        return `The selected component is set to expire on ${dayjs(
          end_date,
        ).format('MM/DD/YYYY')}`;
    },
  },
  assemblySavingMessage: {
    successfully: 'has been successfully',
    saveAsDraft: 'saved as draft.',
    saved: 'saved.',
  },
  errors: {
    bannerUnavailable: 'There is no banner to make visible.',
    maxLength20: 'Must be less than 20 characters.',
    required: 'Required',
    sectionPermission: `You don't have permission to edit this section.`,
    notSupported:
      'Oh no! We can’t read your data source because it is incompatible and not supported.',
    tryAgain: 'Please select another date or time and try again.',
    renderError:
      'An error occurred while rendering this component. Please refresh the page and try again.',
    maxLength: (length: number) =>
      `Character limit reached reduce characters to under ${length}`,
    duplicateItem: 'Error: An item with the same unique field already exists',
    duplicatedName: 'Duplicate name please enter a different name',
    startDateEndDateBefore: (first_date: string, second_date: string) =>
      `${first_date} cannot be before ${second_date}:`,
    startDateEndDateAfter: (first_date: string, second_date: string) =>
      `${first_date} cannot be after ${second_date}:`,
    startAndEndDateRequired: 'Start date and end date are required',
    failedTo: (action: string, resource: string, reason: string) =>
      `Failed to ${action} ${resource}${reason ? `: ${reason}` : ''}`,
    invalidDateFormat: (date: 'start_date' | 'end_date') =>
      `Invalid ${date.replace('_', ' ')} format.`,
    startDateBeforeToday: 'Start date cannot be before today.',
  },
  overrides: {
    override: 'Overrides',
    brandMessageOnCOD: 'Brand messaging on COD',
    completeOrderDisplay: 'Complete Order Display',
    orderConfirmOverrides: 'Order confirmation overrides',
    displayRMHCMessage: 'RMHC',
  },
  calendarDropDownMenuItem: {
    default: 'Default',
    rotation: 'Rotation',
    takeover: 'Takeover',
    customerEnters: 'Customer enters drive-thru',
    orderIsMade: 'Order is made',
    purchaseConfirmed: 'Purchase is confirmed',
  },
  locationsOfGroupText: 'Locations in this group',
  locationGroupTab: {
    groupName: 'Group name',
    noOfLocations: 'No. of locations',
    channel: 'Channel',
  },
  previewLocations: 'Preview locations',
  noLocationChangesToSync: 'No location changes to sync.',
  syncActionMessage: (count: number, verb: string) =>
    `${count} location${count > 1 ? 's' : ''} ${
      count > 1 ? 'are' : 'is'
    } ${verb}.`,
  deleteGroupTooltip: 'You cannot delete a group that has locations assigned',
  successfullyUpdateNameLocationGroup: 'name has been successfully updated.',
  deleteModalSubtitle: 'This location group will be permanently deleted.',
  inputFieldError: 'Location group name cannot be empty',
  imageBadging: 'Image badging',
  ownerOperator: {
    allowOwnerOperatorInfoText:
      'Allow Owner/Operators to replace a component with this component.',
    allowOwnerOperatorSubheader:
      'Allow Owner/Operators to override this section',
    allowOwnerOperatorSheduleComponentSubheader:
      'Allow Owner/Operators to override this schedule component at their location.',
    allowOwnerOperatorText:
      'Allow Owner/Operators to replace a component with this component.',
    ownerOperatorHeader: 'Owner/Operator permissions',
    ownerOperatorTags: 'Owner/Operator tags',
    CODMessaging: 'COD messaging',
  },
  dyProductDescription: `Products for Recommended and Suggestions are automatically selected based on analytics`,
  tooltipText: 'Content Manager has turned off this daypart for this day.',
  warningTooltipText:
    'There are two or more dayparts with the same start time.',
  warningAmberTwoToneIconText: 'Contains unpublished products',
  variationAssembly: 'Variation assembly',
  viewLocationGroup: 'View location group',
  selectChannel: 'Select channel',
  softSellDate: 'Soft sell date',
  fullHeader: 'Full header',
  reducedHeader: 'Reduced header',
  productAnimation: {
    title: 'Animation',
    typeSelectorText: 'Animate elements separately',
    styleText: 'Animation style',
    animationTypes: {
      fadeIn: 'Fade in',
    },
    startFrameMsText: 'Start frame ms',
    endFrameMsText: 'End frame ms',
    productName: 'Product name',
    priceLabelStyling: 'Price label styling',
    price: 'Price',
    nutrition: 'Nutrition',
    alertMessage:
      'Animation settings are applicable only when a video file is selected for background.',
  },
  productLabel: 'Product label',
  xAxis: 'X-axis',
  yAxis: 'Y-axis',
  zIndex: 'Z-index',
  width: 'Width',
  height: 'Height',
  left: 'Left',
  right: 'Right',
  center: 'Center',
  bottom: 'Bottom',
  leftPosition: 'Left position',
  topPosition: 'Top position',
  position: 'Position',
  defaultComponent: 'Default component',
  additionalComponent: 'Additional component',
  backupComponent: 'Backups',
  zipCode: 'Zip/Postal code',
  baseSettings: 'Base settings',
  accessibilitySettingDefaultTheme:
    'Which default values do you want to change?',
  subheaderAndPill: 'Subheader & pill',
  comboComponentStyling: 'Combo component styling',
  comboOrderNumber: 'Combo order number',
  comboBadgeAndBar: 'Combo badge & bar',
  imageBadge: 'Image badge',
  selectComboVariant: 'Select combo variant',
  variantLabel: 'Variant label',
  variantPrice: 'Variant price',
  variantNutrition: 'Variant nutrition',
  autoScale: 'Autoscale',
  defaultComponentTheme: 'Default component theme',
  showProductVariantText: 'Show the following product variants',
  noSizeForVariantText: 'There are no sizes for this product',
  displayFlavorCycling: 'Display flavor cycling',
  changeThemeInfoText:
    'If you change the theme, please review the default assets are correct for existing products.',
  columnStyle: 'Column style',
  numberOfImages: 'Number of images',
  horizontal: 'Horizontal',
  vertical: 'Vertical',
  imageSource: 'Image Source',
  titleTextAndStyling: 'Title, text & styling',
  headerTextAndStyling: 'Header, text & styling',
  donationLabel: 'Donation label',
  componentSettingInfo:
    'You have changed this component, any modified styling will no longer follow the theme settings in accessibility settings.',
  selectThemeInfo:
    'If you change the theme, please review the default assets are correct for existing products.',
  imageBelowText: 'Image Below Text',
  invalidLayoutConfig: 'Invalid layout configuration string',
  disableScreenTitle:
    'Screen inherits dynamic sections from the default layout and content will be restricted from enabled COD screens.',
  none: 'None',
  inline: 'In-line',
  multiline: 'Multi-line',
  removeImage: 'Remove image',
  alacarteLabel: 'à la carte label',
  alacartePrice: 'à la carte price',
  alacarteNutrition: 'à la carte nutrition',
  alacarteStyling: 'à la carte styling',
  selectProductVariationInfo:
    'Show the following product variants, ◉ toggle on the primary variant to show image',
  defaultLayout: 'Default layout',
  showFlavors: 'Show the following flavors',
  showProductInfo: 'Show the following product info',
  knownUser: 'Known user',
  mobileUser: 'Mobile user',
  unknownUser: 'Unknown user',
  messaging: 'Messaging',
  assemblyRequired: 'Assembly is required',
  selectAssembly: 'Select assembly',
  assembliesComponentsAlert:
    'Assemblies with components that will expire within 24 hours cannot be selected for scheduling.',
  imageSection: 'Image section',
  thankYouComponent: {
    knownUser: 'Known user',
    mobileUser: 'Mobile user',
    unknownUser: 'Unknown user',
    messaging: 'Messaging',
    imageSection: 'Image section',
    adConfiguration: 'Ad configuration',
    unknownUserWithAds: 'Unknown user with ads',
    knownUserWithAds: 'Known user with ads',
  },
  contentSelection: 'Content selection',
  singleShelf: 'Single shelf',
  recommended: 'Recommended',
  suggestions: 'Suggestions',
  sort: 'Sort',
  itemsPerPage: 'Items per page',
  clearSection: 'Clear selection',
  locationGroupsAreSelected: 'location groups are selected',
  locationGroupIsSelected: 'location group is selected',
  noNsnsAssigned: 'No NSNs assigned',
  defaultAssemblyAssign: 'Assign a default assembly to daypart',

  componentTypes: {
    singleShelf: 'Single Shelf',
    promotionalSpace: 'Promotional Space',
    listingWithOptionalVisuals: 'Listing with Optional Visuals',
    listingWithVisualGallery: 'Listing with Visual Gallery',
    comboStyle: 'Combo Style',
    cod: 'COD',
    codThankYou: 'Thank You Screen',
    disclaimer: 'Disclaimer',
    promotionalComponent: 'Promotional Component',
  },
  ...applicationModals,
  ...productsLabels,
  ...categoryLabels,
  replaceAssetModal: {
    title: 'Are you sure want to replace asset image?',
    subTitle:
      'Once you replace the following asset image this will affect all active components and changes cannot be undone.',
  },
  scheduleComponent: {
    label: 'Schedule component',
    name: 'Schedule component name',
    scheduleNameError: 'Invalid scheduler name, exceeds 255 characters',
    scheduleNameField: 'Schedule name',
    scheduleNameFieldValidationMessage: 'Schedule name is required',
    stepsCompleted: 'All steps completed',
    saveAndNext: 'Save & Next',
    startDate: 'Start Date',
    lastModified: 'Last modified',
    selectedAssemblyText: 'assemblies selected on page.',
    endDate: 'End Date',
    scheduledOrDefault: 'Scheduled or default',
    selectForSchedule: 'Select for schedule',
    duration: 'Duration (seconds)',
    steps: {
      componentTitle: 'Select a component to schedule',
      assembly: 'Select assemblies',
      assemblyTitle: 'Select assemblies to schedule to',
      placementAndSchedule: 'Placement & schedule',
      placementAndScheduleTitle:
        'Select the section  you want to schedule the component to',
      scheduleDetails: 'Schedule details',
    },
    swapHorizOutlinedIconTooltip: 'Components available to replace',
    viewLocationsModalSearchBarPlaceholder: 'Tag name',
    contentScheduling: 'Content scheduling',
    editContentScheduling: 'Edit schedule content',
    createSectionTips: 'Click schedule component to start creating sections.',
    emptySection: 'No sections created yet.',
    scheduleDetails: 'Schedule details',
    screenGridSettingTooltip:
      'Delete created sections to edit screens grid layout',
    screenOrientation: 'Screen orientation',
    removeComponentTitle: 'Are you sure you want to remove this component?',
    previewPosition: 'Preview position',
    positionDropdownAll: 'All',
    screenDropdownAll: 'All screens',
    swapComponent: 'Swap component',
    scheduleAdditionalComponent: 'Schedule additional component',
    deleteSection: 'Delete section',
    deleteSectionDescription:
      'Section currently contains added components which will be removed upon deletion of section.',
    outdoorDynamicScreenAlert:
      'Components on this screen may be hidden by customer triggers.',
    additionalLocationGroups: 'Additional location groups',
    additionalLocationGroupsOptional: 'Additional location groups (optional)',
    scheduleMultiplLocation:
      'Schedule to multiple location groups within schedule',
    scheduleEndDateTooltip:
      'End date will be prefilled for components that have an expiry date',
    invalidBackupErrorMsg:
      'Select a backup that is valid within the scheduled dates',
    playback: {
      play: 'Play',
      pause: 'Pause',
    },
    preview: {
      noPreviewAvailable: 'No preview available',
      beforeScheduleButton: 'Before schedule',
      afterScheduleButton: 'With schedule',
      sectionClickInfo: 'Click a section to select or clear it',
      noSelectedSectionAvailable: 'Select an item on the board to edit',
      selectedComponentNotEditable: 'Selected component is not editable',
      addOverrideToModifyContent: {
        line1: '+ Add override',
        line2: 'to modify content',
      },
      noValidPromotion: 'No valid promotions found',
      noMatchingComponentsFound: 'No matching components found',
      previewComingSoon: 'Preview',
      expires: 'Expires',
      ongoing: 'Ongoing',
      replace: 'Replace',
      promosAndProducts: 'Promos & products',
      searchPromosAndTags: 'Search Promos & Tags',
      drafting: 'Drafting',
      selectPromo: 'Select promo',
      addOverride: 'Add override',
      maxOverrideModaltitle: "You've reached the override limit",
      maxOverrideModalcontent:
        'You can add up to 5 override modifications at a time. To create another override modification, save or discard your current changes first.',
      deleteExistingOverrideModalTitle: 'Delete existing override?',
      deleteExistingOverrideModalContent:
        'This will remove the existing override.',
      duration: 'Duration',
      makeEditToViewOverrides: 'Make an edit to view overrides',
      previewStartDateMismatch: 'Switch preview date to view this component',
      selectProductToViewAttributes: 'Select a product to view attributes',
      product: 'Product',
      productImage: 'Product Image',
      showImage: 'Show image',
    },
    override: {
      toggleDesc: 'Show what I can edit',
      bulkUpdateScheduleOverridesConfirmationModalTips:
        'All changes will be saved.',
    },
    viewAll: 'View All',
  },
  deleteAssetTooltip: 'You cant delete as this is being used elsewhere.',
  scheduleAssemblyInfo: {
    all: 'All',
    selected: 'selected.',
    selectedOnPage: 'selected on page.',
    assemblies: 'assemblies',
    assembly: 'assembly',
  },
  deleteScheduleAssemblyModal: {
    title: 'Are you sure you want to delete this assembly schedule?',
    subTitle: (assembly_name: string | null) =>
      `Are you sure you want to delete ${assembly_name} from your schedule? If there are overrides on the scheduled assembly they will also be deleted. This cannot be undone.`,
  },
  deleteNewPromoOverrideModal: {
    title: 'Delete new override?',
    subTitle: 'This will remove the new override.',
  },
  successfullyDeleteScheduleAssembly:
    'Assembly schedule has been successfully deleted.',
  successfullyDeleteScheduleItem:
    'Schedule item has been successfully deleted.',
  failedToDeleteScheduleAssembly: 'Failed to delete assembly schedule.',
  deleteAssemblySchedule: 'Delete assembly schedule',
  deleteSchedule: 'Delete schedule',
  noEndDate: 'No end date',
  editSchedule: 'Edit schedule',
  layoutRotation: {
    defaultLayout: 'Default layout',
    maxTimeErrorMessage: 'Duration must not exceed 10 minutes (600 seconds).',
    defaultLayoutInputLabel: 'Start next layer at (seconds)',
    defaultLayoutPreviewButtonLabel: 'Preview default',
    defaultLayoutInputTooltip:
      'The next layer will start play after how many seconds you define, overlaying the default layout.',
    sectionRotations: 'Section rotations',
    sectionRotationsInputLabel: 'Duration (seconds)',
    sectionRotationsInputTooltip:
      'Define how long you want this layer to last before animating to the next layer.',
    sectionRotationsPreviewButtonLabel: 'Preview rotations',
    takeover: 'Takeover',
    takeoverInputLabel: 'Duration (seconds)',
    takeoverInputTooltip:
      'Define how long you want this layer to last before rotating back to only showing the default layout.',
    takeoverPreviewButtonLabel: 'Preview takeover',
    OOdefaultLayoutInputTooltip: 'The next layer will start after 10 seconds',
    OOsectionRotationsInputTooltip: 'This layer will play for 5 seconds',
    OOtakeoverInputTooltip:
      'This layer will play for 5 seconds before rotating back to the default layout',
  },
  variant: 'Variant',
  variants: 'Variants',
  result: 'Result',
  failedToFetchProducts: 'Failed to fetch products',
  globalThemeAlertCaption: (themeName: string = 'every') =>
    `Please update ${themeName} theme in Accessibility settings with the default values. Any unsaved changes will be lost.`,
  manageCategory: 'Manage category',
  newProductCategory: 'New product category',
  newComponentTag: 'New component tag',
  viewProducts: 'View products',
  manageProductCategory: 'Manage product category',
  manageComponentTag: 'Manage component tag',
  ...customerStateLabels,
  ...assemblyAlerts,
  addLayoutsPage: {
    steps: {
      general: 'General',
      screenConfig: 'Screen configuration',
      advanced: 'Advanced',
    },
    title: "Let's create a layout",
    layoutExistsError: 'Layout name for the selected channel already exists.',
    outdoorTwoHorizontalScreensError:
      '2 horizontal screens with an outdoor layout type is not supported.',
    disclaimerError:
      'You must enable disclaimer on at least 1 screen in order to proceed.',
    successMessage: (name: string) => `${name} has been successfully created.`,
    errorMessage: 'Error creating layout.',
    buttons: {
      back: 'Back',
      cancel: 'Cancel',
      next: 'Next',
      save: 'Save',
    },
  },
  assemblyLayouts: {
    generalSettings: {
      title: 'General settings',
      layoutName: 'Layout name*',
      layoutNamePlaceholder: 'Enter layout name',
      channelLabel: 'Channel*',
      channelPlaceholder: 'Select a channel',
      screenConfigTitle: 'Screen configuration',
      layoutTypeTitle: 'Layout type',
      channelTypeLabel: 'Channel type',
      fieldRequired: 'This field is required.',
      layoutNameMaximum: 'Layout name cannot exceed 100 characters.',
      displayPositionLabel: 'Position of the Customer Order Display',
      displayPositionName: 'codPosition',
      leftDisplayPositionLabel: 'Left side of screen',
      rightDisplayPositionLabel: 'Right side of screen',
    },

    screenSetup: {
      title: 'Screen set-up',
      gridLabel: 'Grid*',
      disclaimerLabel: 'Enable disclaimer',
      placementLabel: 'Placement',
      sizeLabel: 'Size',
      orientation: 'Orientation',
      codMessage:
        'To enable COD or Dynamic area, the menu screen must use the 3x4 grid and enable disclaimer',
      CodDYRWarning: (screenName: string) =>
        `To enable COD or Dynamic area, the ${screenName} must use the 3X4 grid and enable disclaimer`,
    },

    advancedSettings: {
      title: 'Advanced settings',
      defaultLayoutTitle: 'Default layout',
      layoutTypes: 'Known, Unknown, and Mobile',
      enableCustomerOrderDisplay: 'Enable customer order display',
      stepOneTitle: '1. Customer enters drive-thru',
      enableRecommendations: 'Enable recommendations',
      enableDynamicArea: 'Enable dynamic area',
      stepTwoTitle: '2. Order is made',
      enableSuggestions: 'Enable suggestions',
      enableCodTriggeredArea: 'Enable COD triggered area',
      enablePromos: 'Enable COD promos',
      stepThreeTitle: '3. Purchase is confirmed',
      enableThankYouScreen: 'Enable thank you screen',
      enableRotation: 'Section rotation',
      enableTakeover: 'Takeover',
      enableCustomerTriggerEventsInfo:
        'Enable this function in customer triggered events',
      functionNotAvailableInfo: 'This function is not available',
      dynamicSection: 'Dynamic section',
      rotationSection: 'Rotation section',
      message:
        'Some screens may inherit dynamic sections from the default layout and content will be restricted from selected COD screens.',
      notAvailableWarning: 'This function is not available',
      sectionRotationTakeoverDisabledTooltip:
        'Section Rotation and Takeover are not supported for vertical layouts due to COD and Confirmation layout rules.',
      sectionRotationDisabledTooltip:
        'Section rotation is not supported for this layout',
      takeOverDisabledTooltip: 'Takeover is not supported for this layout',
    },
  },
  editLayoutsSidebar: {
    defaultLayout: 'Default layout',
    screenRotations: 'Section rotations',
    generalDetails: 'General details',
    takeOver: 'Takeover',
    recommendation: 'Recommendations',
    dynamicArea: 'Dynamic area',
    suggestion: 'Suggestions',
    codTriggeredArea: 'COD triggered area',
    combo: 'Combo',
    cod: 'COD',
    confirmation: 'Confirmation',
    promoOne: 'Promo 1',
    promoTwo: 'Promo 2',
    thankYou: 'Thank You',
    layoutName: 'Layout name',
    saveScreen: 'Save screen',
    backToDefaultLayout: 'Back to default layout',
    createSectionAlert: 'Click and drag to create a section',
    sectionCreateSuccess: (index: string) =>
      `Screen ${index} successfully saved`,
    takeOverAlert: 'Takeovers cannot skip screens in between',
    takeOverLabel: 'Select screens for takeover',
    dragSectionAlert: 'Click “create” to start creating sections',
    rotationGridChangeAlert:
      'Grid has changed, you must fully populate this screen with sections in order to save.',
    defaultLayoutDragSectionAlert:
      'Reference the default layout as you create your section rotation layer',
    warnScreenConfigSave:
      'Applying new screen configurations may cause your created sections to be lost.',
    warnRotationScreenConfigSave:
      'If you change the grid choice, then you have to fully populate the screen with sections.',
    errorLastDisclaimer:
      'Can not disable the only disclaimer. Enable disclaimer on another screen to disable this one.',
    previewCODStates: 'Please fill all screen to preview COD states.',
    sectionAlert: `Once you click “create sections” to get started, you can drag on the
    grid to create a section.`,
    createSections: 'Create sections',
    configureScreen: (screenName: string) => `Configure ${screenName}`,
    successMessage: (index: number) =>
      `Screen ${index + 1} configuration has been successfully updated.`,
    saveAsDraftMessage: (name: number) =>
      `${name} has been successfully saved as draft`,
    saveAsReadyMessage: (name: number) => `${name} has been successfully saved`,
    errorMessage: 'Error updating configuration.',
    sectionDisabledTooltip:
      'Complete the default layout first before moving on to the next layers',
    layoutNameRequired: 'Layout name is required',
    layoutNameMaximum: 'Layout name cannot exceed 100 characters.',
  },
  publicationPage: {
    product: {
      lastPublishedTooltip:
        'Once a publication is complete, refresh to see the updated timestamp',
    },
  },
  variationSizeModelTitle: 'Please provide a name for variation type',
  formErrorMsg: {
    required: (field: string) => `${field} is required.`,
    minimumAndMaximum: (min: number, max: number) =>
      `Must be between ${min} and ${max}`,
  },
  bulkReplaceComponentPage: {
    existingComponent: 'Existing component',
    newComponent: 'New component',
    loadingComponent: 'Loading component...',
    collapseContent: 'Collapse content',
    sortBy: 'Sort by',
    channelType: 'Channel type',
    numberOfSchedules: 'Number of schedules',
    outdoor: 'Outdoor',
    locationGroupPrefix: (index: number) => `Location group ${index}`,
    reviewChanges: {
      replacementChangesInfo: (count = 0) =>
        `Replacement component to ${count} location group${
          count === 1 ? '' : 's'
        }`,
      overrideInfo: 'Component has overrides that will be replaced',
      replacedInfo: 'Component will be replaced',
      notReplacedInfo: `Component won't be replaced`,
    },
  },
  cod_position_alert: (codPosition?: string) =>
    `Drive through is configured for ${codPosition || 'left'} hand drive`,
  previewDaypart: 'Preview daypart',
  customerTriggeredEventsPage: {
    select: 'Select',
    selectComponentForDayparts: 'Select component for dayparts',
    cancel: 'Cancel',
    save: 'Save',
    remove: 'Remove',
    customerTriggeredEvents: 'Customer triggered events',
    noComponentTooltip: 'Missing component or daypart selection',
    addComponent: 'Add component',
    deleteComponent: 'Delete component',
    swapComponent: 'Swap component',
    filteredToShowOnlyComponentsWithNoEndDate:
      'This view is filtered to show only components with no end date.',
    selectScreenConfiguration: 'Select screen configuration',
    driveThruDirection: (codPosition?: string) =>
      `Drive-thru is configured for ${codPosition} hand drive`,
    screenLayoutType: 'Screen Layout type',
    screen: 'Screen',
    pairedItems: 'Paired items',
    dyrNotAvailableOn: (screenName: string) =>
      `Dynamic area is not available on ${screenName}.`,
    filteredToShowOnlyComponentsWithNoEndDateDynamicArea:
      'This view is filtered to show only components with no end date. Only Recommended components are available.',
    filteredToShowOnlyComponentsWithNoEndDateCodTriggeredEvents:
      'This view is filtered to show only components with no end date. Only Suggestions components are available.',
  },
  customerTriggeredEventsModal: {
    title: 'Are you sure you want to save your changes?',
    subTitleWithPublish:
      'After you save your changes you can publish to your media players.',
    subTitle:
      'After you save your changes the settings will be applied to new assemblies and layouts.',
  },
  locationAssemblyModal: {
    replace: (title: string) => `Replace ${title} assembly`,
    add: (title: string) => `Add ${title} an assembly`,
    schedule: (title: string) => `Schedule ${title} assembly`,
  },
  clearFilter: 'Clear filters',
  filters: 'Filters',
  selectTags: 'Select tags',
  selectZone: 'Select Zone',
  selectSubzone: 'Select Subzone',
  variationCodeNotFound: 'Product ID not found, please start again.',
  dytemplates: 'DY templates',
  deleteTemplateTitle: 'Are you sure you want to delete?',
  deleteTemplateSubTitle: 'This DY display will be permanently deleted.',
  helpButtonTitle: 'For all support needs, call McDonald’s Support Desk',
  allowAds: 'Allow ads',
  canaryBannerMessage:
    'Creative Center is being upgraded now. Content can still be published. Updates will display on menu boards after your selected location(s) are upgraded.',
  canaryAcknowledgeModal: {
    title: 'Creative Center is currently being upgraded',
    subTitle:
      'This is a phased process to complete all locations. Content can still be published. Publications will be visible on menu boards after your selected location(s) have been upgraded to the latest version.',
    dontShowAgain: 'Do not show again',
    acknowledge: 'Got it',
  },
};

export type ErrorMessage = string | ((props?: Record<string, any>) => string);

export const errorMessages: Record<string, ErrorMessage> = {
  ERR_ASSEMBLY_ID_MISSING: 'assembly_id must be provided.',
  ERR_STATUS_NOT_STAGED: 'status cannot be other than staged.',
  ERR_DUPLICATE_DISPLAY_NAME: 'Display name must be unique.',
  ERR_ASSEMBLY_COMPONENT_MISSING:
    'All assemblies must have selected from component.',
  ERR_COMPONENTS_IDENTICAL: 'From and To components cannot be the same.',
  ERR_FROM_COMPONENT_NOT_FOUND: 'From component not found.',
  ERR_TO_COMPONENT_NOT_FOUND: 'To component not found.',
  ERR_TO_COMPONENT_EXPIRED: 'To component is expired.',
  ERR_TO_COMPONENT_END_DATE_INVALID:
    'To component cannot have an end date if from component does not.',
  ERR_COMPONENT_END_DATE_ORDER_INVALID:
    'To component end date must be greater than from component end date.',
  ERR_STAGED_REQUEST_EXISTS:
    'Staged bulk replace request already exists for the same assembly and from component.',

  ERR_ID_MISSING: 'Must provide ID.',
  ERR_INVALID_ID: 'Invalid ID.',
  ERR_BULK_REPLACE_REQUEST_NOT_FOUND: 'Bulk Replace Request not found.',
  ERR_COMPONENT_EXPIRED: ({
    component_name,
  }: { component_name?: string } = {}) =>
    `Component ${component_name} has expired.`,
  ERR_BULK_REPLACE_ALREADY_SAVED: 'Bulk Replace Request is already saved.',

  ERR_ASSEMBLY_IDS_NOT_ARRAY: 'assembly_ids must be an array',
  ERR_INVALID_ASSEMBLY_IDS: 'Invalid assembly_ids',
  ERR_INVALID_FROM_COMPONENT_ID: 'Invalid from_component_id',
  ERR_DISPLAY_NAME_TOO_LONG: 'Display name too long',
};
